import React, { useEffect, useState } from "react";
import { Button, Spin, Tooltip } from "antd";
import axios from "axios";
import moment from "moment";
import {
  AiOutlineAppstore,
  AiOutlineDollarCircle,
  AiOutlineFileText,
  AiOutlineReload,
} from "react-icons/ai";
import { FaBusAlt, FaChalkboardTeacher, FaUserGraduate } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../../nestserver";
import { selectUser } from "../../../../../redux/slices/auth.slice";
import StickySectionNav from "./StickySectionNav";
import useScrollSpy from "./useScrollSpy";
import { SECTION_COLORS } from "./chartTheme";
import OverviewSection from "./sections/OverviewSection";
import FinanceSection from "./sections/FinanceSection";
import StudentsSection from "./sections/StudentsSection";
import AdmissionsSection from "./sections/AdmissionsSection";
import StaffSection from "./sections/StaffSection";
import OperationsSection from "./sections/OperationsSection";

const SECTIONS = [
  {
    id: "overview",
    label: "Overview",
    icon: <AiOutlineAppstore size={15} />,
    color: SECTION_COLORS.overview,
  },
  {
    id: "finance",
    label: "Finance & Fees",
    icon: <AiOutlineDollarCircle size={15} />,
    color: SECTION_COLORS.finance,
  },
  {
    id: "students",
    label: "Students & Attendance",
    icon: <FaUserGraduate size={14} />,
    color: SECTION_COLORS.students,
  },
  {
    id: "admissions",
    label: "Admissions",
    icon: <AiOutlineFileText size={15} />,
    color: SECTION_COLORS.admissions,
  },
  {
    id: "staff",
    label: "Staff",
    icon: <FaChalkboardTeacher size={14} />,
    color: SECTION_COLORS.staff,
  },
  {
    id: "operations",
    label: "Operations",
    icon: <FaBusAlt size={14} />,
    color: SECTION_COLORS.operations,
  },
];

const SECTION_IDS = SECTIONS.map((s) => s.id);

const getCurrentAcademicYear = () => {
  const today = new Date();
  const curYear = today.getFullYear();
  const curMonth = today.getMonth() + 1;
  const startYear = curMonth >= 6 ? curYear : curYear - 1;
  const endYearShort = String(startYear + 1).slice(-2);
  return `${startYear}-${endYearShort}`;
};

const Iskool360Dashboard = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const academicYear = getCurrentAcademicYear();

  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    totalParents: 0,
    totalClasses: 0,
    totalSubjects: 0,
    pendingApplications: 0,
    applicationsThisMonth: 0,
    pendingTransactions: 0,
    pendingDiscounts: 0,
    pendingCertificateRequests: 0,
    transport: { buses: 0, activeRoutes: 0, transportStudents: 0 },
    announcementsThisMonth: 0,
    trends: { totalStudents: 0, totalTeachers: 0, totalClasses: 0 },
  });
  const [attendance, setAttendance] = useState({ trend: [], today: {} });
  const [fee, setFee] = useState({
    expected: 0,
    collected: 0,
    remaining: 0,
    discount: 0,
    studentFees: 0,
    studentsWithDues: 0,
    collectionRate: 0,
    statusDistribution: {},
    monthlyCollections: [],
    trends: { collected: 0, remaining: 0 },
  });
  const [distribution, setDistribution] = useState({ byClass: [], byGender: [] });
  const [admissions, setAdmissions] = useState({ byStatus: {}, monthlyTrend: [] });
  const [activity, setActivity] = useState({ payments: [], announcements: [] });
  const [staffOps, setStaffOps] = useState({
    totalInstructors: 0,
    attendanceToday: {
      onTime: 0,
      late: 0,
      onLeaveOrPermission: 0,
      notCheckedIn: 0,
      total: 0,
    },
    attendanceTrend: [],
    leaveRequests: {
      pending: 0,
      approvedThisMonth: 0,
      rejectedThisMonth: 0,
      pendingByType: { permission: 0, leave: 0, sick: 0 },
    },
    departmentDistribution: [],
  });

  const { activeId, scrollToSection } = useScrollSpy(SECTION_IDS, {
    ready: !initialLoading,
  });

  const fetchDashboard = async () => {
    setRefreshing(true);
    const headers = {
      Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
    };
    try {
      const [
        overviewRes,
        attendanceRes,
        feeRes,
        distributionRes,
        admissionsRes,
        activityRes,
        staffOpsRes,
      ] = await Promise.all([
        axios.get(`${serverUrl}/school-analytics/overview`, { headers }),
        axios.get(`${serverUrl}/school-analytics/attendance-trend?days=14`, {
          headers,
        }),
        axios.get(`${serverUrl}/school-analytics/fee-snapshot?year=${academicYear}`, {
          headers,
        }),
        axios.get(`${serverUrl}/school-analytics/students-distribution`, {
          headers,
        }),
        axios.get(`${serverUrl}/school-analytics/admissions-funnel`, { headers }),
        axios.get(`${serverUrl}/school-analytics/recent-activity`, { headers }),
        axios.get(`${serverUrl}/school-analytics/staff-ops`, { headers }),
      ]);
      if (overviewRes.data?.result) setOverview(overviewRes.data.result);
      if (attendanceRes.data?.result) setAttendance(attendanceRes.data.result);
      if (feeRes.data?.result) setFee(feeRes.data.result);
      if (distributionRes.data?.result)
        setDistribution(distributionRes.data.result);
      if (admissionsRes.data?.result) setAdmissions(admissionsRes.data.result);
      if (activityRes.data?.result) setActivity(activityRes.data.result);
      if (staffOpsRes.data?.result) setStaffOps(staffOpsRes.data.result);
    } catch (error) {
      console.error("Error fetching Is-Kool 360 metrics:", error);
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-slate-50 min-h-screen p-8">
      {/* ── Hero header ── */}
      <div
        className="rounded-2xl p-6 md:p-8 mb-4 text-white shadow-md"
        style={{
          background:
            "linear-gradient(120deg, #03212B 0%, #09617E 55%, #0C86AE 100%)",
        }}
      >
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-white">
              Is-Kool-360&deg;
            </h1>
            <p className="text-sky-100 mt-1">
              Your school live view at a glance — {user?.school?.name || "your school"}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-right">
              <div className="text-sm font-semibold text-white">
                {moment().format("dddd, DD MMMM YYYY")}
              </div>
              <div className="text-xs text-sky-100">
                Academic Year {academicYear}
              </div>
            </div>
            <Tooltip title="Refresh metrics">
              <Button
                shape="circle"
                icon={
                  <AiOutlineReload
                    className={refreshing ? "animate-spin" : ""}
                  />
                }
                onClick={fetchDashboard}
                className="flex items-center justify-center"
              />
            </Tooltip>
          </div>
        </div>
      </div>
      <StickySectionNav
        sections={SECTIONS}
        activeId={activeId}
        onSelect={scrollToSection}
      />
      {initialLoading ? (
        <div className="flex justify-center items-center h-96">
          <Spin size="large" tip="Assembling your 360° view..." />
        </div>
      ) : (
        <div
          className={`transition-opacity duration-300 ${
            refreshing ? "opacity-60 pointer-events-none" : ""
          }`}
        >
          <OverviewSection
            overview={overview}
            attendance={attendance}
            fee={fee}
            staffOps={staffOps}
            activity={activity}
            distribution={distribution}
            onNavigate={navigate.push}
            onJumpToSection={scrollToSection}
          />
          <FinanceSection
            fee={fee}
            overview={overview}
            academicYear={academicYear}
            onNavigate={navigate.push}
          />
          <StudentsSection
            overview={overview}
            attendance={attendance}
            distribution={distribution}
          />
          <AdmissionsSection overview={overview} admissions={admissions} />
          <StaffSection
            overview={overview}
            staffOps={staffOps}
            onNavigate={navigate.push}
          />
          <OperationsSection overview={overview} onNavigate={navigate.push} />
        </div>
      )}
    </div>
  );
};

export default Iskool360Dashboard;
